import { useEffect, useRef, useState, useCallback } from 'react';
import { appConfig } from '../utils/config';
interface UseActivityBasedTimeoutReturn {
  isRefreshing: boolean;
  showWarningModal: boolean;
  warningSecondsRemaining: number;
  warningType: 'idle' | 'session';
  handleStayLoggedIn: () => void;
  dismissWarning: () => void;
}
interface SessionState {
  timeUntilIdleTimeout: number;
  timeUntilAbsoluteTimeout: number;
  warningThreshold: boolean;
  sessionDurationMinutes: number;
  sessionVersion: number;
  isNearExpiry: boolean;
}

export function useActivityBasedTimeout(handleLogout: () => void): UseActivityBasedTimeoutReturn {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loginStatus, setLoginStatus] = useState(() => localStorage.getItem('loggedIn') === 'true');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningSecondsRemaining, setWarningSecondsRemaining] = useState(0);
  const [warningType, setWarningType] = useState<'idle' | 'session'>('idle');
  
  const lastCallRef = useRef<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionStateRef = useRef<SessionState | null>(null);
  const showWarningModalRef = useRef<boolean>(false);
  const isRefreshingRef = useRef(false);
  const lastSuccessfulHeartbeatRef = useRef<number>(0);
  const pendingActivityRef = useRef<boolean>(false);
  // Suppress server-driven warnings for a short grace period after user interaction
  const suppressWarningUntilRef = useRef<number>(0);
  // Suppress re-showing when user dismisses the modal until suppression expires
  const dismissSuppressRef = useRef<boolean>(false);
  const dismissLogoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const WARNING_SUPPRESS_MS = 30 * 1000; // 30 seconds grace after user clicks Continue/Dismiss
  
  // Constants for heartbeat intervals
  const REFRESH_CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds normally (reduce requests)
  const WARNING_CHECK_INTERVAL_MS = 10 * 1000; // Check every 10 seconds during warning (urgent)
  
  // Debug flag - controlled only via localStorage.DEBUG_SESSION for simplicity
  let DEBUG = false;
  try {
    DEBUG = typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_SESSION') === 'true';
  } catch (e) {
    DEBUG = false;
  }
  // Helper to check login status
  const isLoggedIn = () => localStorage.getItem('loggedIn') === 'true';
  // SIMPLE 1-MINUTE SESSION HEARTBEAT
  // Call /api/auth/refresh - use recordActivity param to tell backend if this is real activity
  const sessionHeartbeat = useCallback(async (forceCall = false, recordActivity = false): Promise<SessionState | null> => {
    // Don't call if not logged in, already calling, or on logout page
    const currentlyLoggedIn = isLoggedIn();
    const currentlyRefreshing = isRefreshingRef.current;
    const onLogoutPage = window.location.pathname.includes('/logout');
    
    if (DEBUG) console.log('[Session] 🔍 Pre-flight checks:', {
      currentlyLoggedIn,
      currentlyRefreshing,
      onLogoutPage,
      forceCall,
      recordActivity
    });
    
    if (!currentlyLoggedIn || currentlyRefreshing || onLogoutPage) {
      if (DEBUG) console.log('[Session] 🛑 Aborting heartbeat:', { currentlyLoggedIn, currentlyRefreshing, onLogoutPage });
      return null;
    }
    if (DEBUG) console.log(`[Session] 💓 Session heartbeat starting... (recordActivity: ${recordActivity})`);
    
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    try {
      if (DEBUG) console.log('[Session] ➤ Sending POST /api/auth/refresh', { url: `${appConfig.api.baseUrl}/api/auth/refresh`, recordActivity });
      const response = await fetch(
        `${appConfig.api.baseUrl}/api/auth/refresh`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'cookie-based-auth', // Required for API Gateway to invoke authorizer with cookie-only auth
          },
          body: JSON.stringify({ recordActivity }), // Send in body instead of header
        }
      );
      if (DEBUG && !response.ok) {
        try {
          const text = await response.clone().text();
          console.warn('[Session] ↩️ POST /api/auth/refresh non-OK', { status: response.status, body: text });
        } catch (e) {
          console.warn('[Session] ↩️ POST /api/auth/refresh non-OK (no body)', { status: response.status });
        }
      }
      if (!response.ok) {
        // SIMPLIFIED: Any error (401/403/429/5xx/etc) triggers logout immediately
        if (DEBUG) console.error(`[Session] ❌ Non-OK response (${response.status}) - forcing logout`);
        // Clear transient auth flags BEFORE logout to ensure clean state
        try { localStorage.removeItem('justSignedIn'); } catch {}
        try { localStorage.removeItem('authInProgress'); } catch {}
        try { localStorage.removeItem('authInProgressAt'); } catch {}
        // Also clear any stored idle expiry markers so they don't persist after auto-logout
        try { localStorage.removeItem('idleExpiresAt'); } catch {}
        try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}
        // NOTE: id_token is NOT cleared here - let handleLogout() manage it
        // to ensure Okta logout flow can use it without 400 errors
        handleLogout();
        return null;
      }
      const data = await response.json();
      
      if (DEBUG) console.log('[Session] ✅ Heartbeat successful:', data.sessionState);
      // Record last successful heartbeat timestamp to avoid duplicate activity-triggered calls
      lastSuccessfulHeartbeatRef.current = Date.now();
      // Clear any queued activity now that we've successfully refreshed
      pendingActivityRef.current = false;
      try {
        localStorage.setItem('lastSessionHeartbeat', lastSuccessfulHeartbeatRef.current.toString());
      } catch {
        // ignore storage failures
      }
      
      // Update session state cache
      sessionStateRef.current = data.sessionState;
      
      // Update localStorage with server time for cross-tab sync
      if (data.serverTime && data.expiresIn) {
        const expiresAt = (data.serverTime + data.expiresIn) * 1000;
        localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      }
      
      if (data.sessionCreated) {
        localStorage.setItem('sessionCreated', (data.sessionCreated * 1000).toString());
      }
      // Check if we should show warning based on backend state
      const { sessionState } = data;
      const now = Date.now();
      const suppressed = suppressWarningUntilRef.current > now;

      // Backend tells us exactly when to show warnings. If the user just
      // interacted (clicked Continue/Dismiss) we suppress immediate re-show
      // for a short grace window to avoid flicker and confusing UX.
      // However, absolute/session warnings are critical; allow them to bypass
      // the short suppression window when the server reports only a small
      // remaining time so the user still gets a chance to sign in.
      const sessionRemaining = sessionState.warningSecondsRemaining || Math.floor(sessionState.timeUntilAbsoluteTimeout || 0);
      const ALLOW_SESSION_BYPASS_S = 180; // allow session warnings to bypass suppression when <= 180s remaining (3 minutes)
      const allowSessionBypass = sessionRemaining <= ALLOW_SESSION_BYPASS_S;

      // Respect any in-progress auth flow or immediate post-signin state to
      // avoid showing the modal during the redirect/code-exchange which can
      // cause flicker. Read the same localStorage flags used in error paths.
      let authInProgressFlag = false;
      let justSignedInRaw: string | null = null;
      let justSignedInRecent = false;
      try {
        authInProgressFlag = localStorage.getItem('authInProgress') === 'true';
        const authInProgressAtRaw = localStorage.getItem('authInProgressAt');
        if (authInProgressFlag && authInProgressAtRaw) {
          const authAt = parseInt(authInProgressAtRaw, 10);
          if (!Number.isNaN(authAt)) {
            const AUTH_PROGRESS_STALE_MS = 2 * 60 * 1000; // 2 minutes
            if ((Date.now() - authAt) > AUTH_PROGRESS_STALE_MS) {
              // stale flag - clear it and treat as not in progress
              try { localStorage.removeItem('authInProgress'); } catch {}
              try { localStorage.removeItem('authInProgressAt'); } catch {}
              authInProgressFlag = false;
            }
          }
        }
        justSignedInRaw = localStorage.getItem('justSignedIn');
        if (justSignedInRaw) {
          const ts = parseInt(justSignedInRaw, 10);
          if (!Number.isNaN(ts)) {
            const POST_SIGNIN_GRACE_MS = 60 * 1000; // 60s grace
            justSignedInRecent = (Date.now() - ts) <= POST_SIGNIN_GRACE_MS;
          }
        }
      } catch {
        authInProgressFlag = false;
        justSignedInRaw = null;
        justSignedInRecent = false;
      }

      // If auth is in progress or we just signed in recently, extend suppression and
      // skip showing warnings to avoid interrupting the auth exchange.
      if ((authInProgressFlag || justSignedInRecent) && (sessionState.showSessionWarning || sessionState.showIdleWarning)) {
        if (DEBUG) console.log('[Session] ⏳ Auth in progress or just signed in (recent) - skipping server warning to avoid flicker', { authInProgressFlag, justSignedInRaw, justSignedInRecent });
        const POST_SIGNIN_GRACE_MS = 60 * 1000; // 60s grace
        suppressWarningUntilRef.current = Math.max(suppressWarningUntilRef.current, Date.now() + POST_SIGNIN_GRACE_MS);
      }

      const showIdle = Boolean(sessionState.showIdleWarning || sessionState.warningThreshold);

      // Allow critical session warnings (low remaining time) to bypass
      // user dismissal suppression so users still get a chance to re-auth.
      // If user has dismissed the modal, respect that dismissal — do not
      // re-open even for critical session warnings. Auto-logout will still
      // occur when the server-side timeout expires.
      if (sessionState.showSessionWarning && !showWarningModalRef.current && (!suppressed || allowSessionBypass) && !authInProgressFlag && !justSignedInRecent && !dismissSuppressRef.current) {
        if (DEBUG) {
          console.log('[Session] ⚠️ Backend indicated session warning needed');
          if (suppressed && allowSessionBypass) console.log('[Session] ⚠️ Suppression bypassed for session warning (low remaining)', { sessionRemaining });
        }
        setWarningType('session');
        setShowWarningModal(true);
        showWarningModalRef.current = true;
        setWarningSecondsRemaining(sessionRemaining || 120);
      } else if (showIdle && !showWarningModalRef.current && !suppressed && !authInProgressFlag && !justSignedInRecent && !dismissSuppressRef.current) {
        if (DEBUG) console.log('[Session] ⚠️ Backend indicated idle warning needed');
        setWarningType('idle');
        setShowWarningModal(true);
        showWarningModalRef.current = true;
        setWarningSecondsRemaining(sessionState.warningSecondsRemaining || 120);
      } else if (showWarningModalRef.current && !showIdle && !sessionState.showSessionWarning) {
        if (DEBUG) console.log('[Session] ✅ Backend says no warning needed - hiding modal');
        setShowWarningModal(false);
        showWarningModalRef.current = false;
      }
      return sessionState;
      
    } catch (error) {
      if (DEBUG) console.warn('[Session] ❌ Heartbeat failed:', error);

      // SIMPLIFIED: Any network/fetch error triggers logout immediately
      if (error instanceof TypeError && error.message && error.message.toLowerCase().includes('fetch')) {
        // If the browser is offline, don't logout yet - wait for connection
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          if (DEBUG) console.log('[Session] 🌐 Browser offline - returning cached state');
          return sessionStateRef.current;
        }

        // Online but fetch failed - treat as error and logout
        if (DEBUG) console.error('[Session] 🌐 Network/CORS error while online - forcing logout');
        try { localStorage.removeItem('justSignedIn'); } catch {}
        try { localStorage.removeItem('authInProgress'); } catch {}
        try { localStorage.removeItem('authInProgressAt'); } catch {}
        // Also clear any stored idle expiry markers so they don't persist after auto-logout
        try { localStorage.removeItem('idleExpiresAt'); } catch {}
        try { sessionStorage.removeItem('idleExpiresAt'); } catch {}
        try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}
        // NOTE: id_token is NOT cleared here - let handleLogout() manage it
        handleLogout();
        return null;
      }

      // For any other error type, also logout
      if (DEBUG) console.error('[Session] ❌ Unknown error type - forcing logout:', error);
      try { localStorage.removeItem('justSignedIn'); } catch {}
      try { localStorage.removeItem('authInProgress'); } catch {}
      try { localStorage.removeItem('authInProgressAt'); } catch {}
      // Also clear idle expiry markers to avoid stale data
      try { localStorage.removeItem('idleExpiresAt'); } catch {}
      try { sessionStorage.removeItem('idleExpiresAt'); } catch {}
      try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}
      handleLogout();
      return null;
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, [handleLogout, DEBUG]);
  // Activity detection - trigger heartbeat on user interaction with throttling
  const recordActivity = useCallback(() => {
    if (!isLoggedIn() || window.location.pathname.includes('/logout')) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastCallRef.current;
    
    // Throttle to max once per 5 seconds (backend rate limit)
    if (timeSinceLastActivity < 5000) {
      if (DEBUG) console.log(`[Session] ⏳ Activity throttled (${Math.floor(timeSinceLastActivity/1000)}s since last)`);
      return;
    }
    lastCallRef.current = now;

    // If we've had a successful heartbeat recently (within the periodic interval),
    // don't fire an immediate request for every click. Instead mark activity as
    // pending and let the periodic heartbeat include it. This keeps requests
    // <= 1 per minute while preserving session extension.
    const timeSinceLastSuccess = Date.now() - lastSuccessfulHeartbeatRef.current;
    if (timeSinceLastSuccess < REFRESH_CHECK_INTERVAL_MS) {
      pendingActivityRef.current = true;
      if (DEBUG) console.log('[Session] 📌 Activity queued for next periodic heartbeat');
      return;
    }

    if (DEBUG) console.log('[Session] 👤 Real user activity detected - recording immediately');
    sessionHeartbeat(false, true); // immediate record if last success was long ago
  }, [sessionHeartbeat, DEBUG]);
  // Periodic session checking
  useEffect(() => {
    const currentLoginStatus = isLoggedIn();
    const isOnLogoutPage = window.location.pathname.includes('/logout');
    
    if (DEBUG) console.log('[Session] 🔍 Periodic check setup:', { 
      currentLoginStatus, 
      isOnLogoutPage, 
      loginStatus,
      pathname: window.location.pathname 
    });
    
    if (!currentLoginStatus || isOnLogoutPage) {
      if (refreshIntervalRef.current) {
        if (DEBUG) console.log('[Session] 🛑 Clearing interval - not logged in or on logout page');
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }
    if (DEBUG) console.log('[Session] ✅ Setting up periodic session checks');
    // Initial heartbeat
    sessionHeartbeat();
    // Periodic heartbeat with dynamic interval
    const checkInterval = showWarningModal ? WARNING_CHECK_INTERVAL_MS : REFRESH_CHECK_INTERVAL_MS;
    
    if (DEBUG) console.log(`[Session] ⏰ Setting interval: ${checkInterval}ms (${checkInterval/1000}s)`);
    
    refreshIntervalRef.current = setInterval(() => {
      const stillLoggedIn = isLoggedIn();
      const stillNotOnLogout = !window.location.pathname.includes('/logout');
      
      if (DEBUG) console.log('[Session] ⏱️ Periodic check:', { stillLoggedIn, stillNotOnLogout });
      
      if (stillLoggedIn && stillNotOnLogout) {
        if (DEBUG) console.log('[Session] 💓 Triggering periodic heartbeat');
        // If activity was queued since the last successful heartbeat, include it
        const includeActivity = pendingActivityRef.current;
        if (includeActivity) {
          if (DEBUG) console.log('[Session] 🔔 Sending queued activity with periodic heartbeat');
        }
        sessionHeartbeat(false, includeActivity).then(() => {
          pendingActivityRef.current = false;
        });
      }
    }, checkInterval);
    return () => {
      if (refreshIntervalRef.current) {
        if (DEBUG) console.log('[Session] 🧹 Cleanup: clearing interval');
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loginStatus, sessionHeartbeat, showWarningModal, DEBUG]); // Add showWarningModal to deps
  // Activity event listeners
  useEffect(() => {
    if (!isLoggedIn() || window.location.pathname.includes('/logout')) {
      return;
    }
    if (DEBUG) console.log('[Session] ✅ Setting up activity listeners');
    
    // Real user activity events - use 'click' instead of 'pointerdown' to avoid DevTools
    const activityEvents = ['click', 'keydown', 'scroll', 'wheel', 'touchstart'];
    
    // Filter non-trusted events (programmatic/DevTools)
    const handleActivity = (e: Event) => {
      if (!e.isTrusted) {
        if (DEBUG) console.log('[Session] ⚠️ Ignoring non-trusted event:', e.type);
        return;
      }
      // Ignore activity from inside the session modal (e.g., Dismiss/Stay/Sign in)
      try {
        const target = e.target as HTMLElement | null;
        if (target && typeof target.closest === 'function') {
          const insideModal = target.closest('#sessionTimeoutWarning_dialog, #sessionTimeoutWarning_paper, .session-timeout-modal');
          if (insideModal) {
            if (DEBUG) console.log('[Session] ⚠️ Ignoring activity originating from session modal');
            return;
          }
        }
      } catch {
        // ignore DOM traversal errors
      }
      recordActivity();
    };
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });
    // Sync last heartbeat across tabs
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'lastSessionHeartbeat' && e.newValue) {
        const ts = parseInt(e.newValue, 10);
        if (!Number.isNaN(ts)) lastSuccessfulHeartbeatRef.current = ts;
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener('storage', handleStorage);
    };
  }, [recordActivity, loginStatus, DEBUG]);
  // Monitor login status changes (both storage events and direct localStorage polling)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'loggedIn') {
        const newLoginStatus = e.newValue === 'true';
        setLoginStatus(newLoginStatus);
        if (DEBUG) console.log(`[Session] 🔄 Login status changed via storage event: ${newLoginStatus}`);
        
        if (!newLoginStatus) {
          // Clear intervals on logout
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
          }
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
        }
      }
    };
    // Also poll localStorage periodically to catch same-tab login changes
    const pollLoginStatus = setInterval(() => {
      const currentStorageStatus = localStorage.getItem('loggedIn') === 'true';
      if (currentStorageStatus !== loginStatus) {
        if (DEBUG) console.log(`[Session] 🔄 Login status changed via polling: ${loginStatus} → ${currentStorageStatus}`);
        setLoginStatus(currentStorageStatus);
      }
    }, 1000); // Check every second
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollLoginStatus);
    };
  }, [loginStatus, DEBUG]);
  // Warning countdown timer
  useEffect(() => {
    if (!showWarningModal) return;
    const countdown = setInterval(() => {
      const sessionState = sessionStateRef.current;
      if (!sessionState) return;
      // Check the correct timeout based on warning type
      const remaining = warningType === 'idle' 
        ? Math.max(0, Math.floor(sessionState.timeUntilIdleTimeout))
        : Math.max(0, Math.floor(sessionState.timeUntilAbsoluteTimeout));
      
      setWarningSecondsRemaining(remaining);
      if (remaining <= 0) {
        setShowWarningModal(false);
        try { localStorage.removeItem('justSignedIn'); } catch {}
        try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}
        handleLogout();
      }
    }, 1000);
    return () => clearInterval(countdown);
  }, [showWarningModal, warningType, handleLogout]);
  // "Stay Logged In" handler
  const handleStayLoggedIn = useCallback(async () => {
    if (DEBUG) console.log('[Session] 👤 User clicked "Stay Logged In"');
    
    // Close modal immediately for better UX
    setShowWarningModal(false);
    // Suppress server-driven warnings for a grace period. If we have server
    // session state, suppress until the server-side remaining timeout has
    // progressed (avoids immediate re-show when nearing absolute limits).
    const ss = sessionStateRef.current;
    if (ss) {
      const remainingMs = (warningType === 'idle' ? ss.timeUntilIdleTimeout : ss.timeUntilAbsoluteTimeout) * 1000;
      const dynamicSuppress = Math.max(WARNING_SUPPRESS_MS, remainingMs + 5000); // add 5s slack
      suppressWarningUntilRef.current = Date.now() + dynamicSuppress;
      if (DEBUG) console.log('[Session] ⛔ Suppressing warnings until', new Date(suppressWarningUntilRef.current).toISOString());
    } else {
      suppressWarningUntilRef.current = Date.now() + WARNING_SUPPRESS_MS;
    }
    
    // Wait for any in-progress refresh to complete
    let attempts = 0;
    while (isRefreshingRef.current && attempts < 10) {
      if (DEBUG) console.log('[Session] ⏳ Waiting for in-progress refresh to complete...');
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (isRefreshingRef.current) {
      if (DEBUG) console.log('[Session] ⚠️ Still refreshing after waiting, forcing activity record anyway');
    }
    
    // Force immediate heartbeat with activity recording
    await sessionHeartbeat(true, true);
    // If there was a pending auto-logout scheduled due to a previous dismiss,
    // clear it because the user chose to stay logged in.
    if (dismissLogoutTimeoutRef.current) {
      clearTimeout(dismissLogoutTimeoutRef.current);
      dismissLogoutTimeoutRef.current = null;
    }
  }, [sessionHeartbeat, DEBUG, warningType]);
  // Dismiss warning
  const dismissWarning = useCallback(() => {
    if (DEBUG) console.log('[Session] 👤 User dismissed warning');
    setShowWarningModal(false);
    // Suppress immediate re-showing of the modal after dismissal (shorter)
    const ss = sessionStateRef.current;
    let suppressMs = Math.max(WARNING_SUPPRESS_MS / 2, 5 * 1000);
    if (ss) {
      const remainingMs = (warningType === 'idle' ? ss.timeUntilIdleTimeout : ss.timeUntilAbsoluteTimeout) * 1000;
      suppressMs = Math.max(suppressMs, Math.min(60 * 1000, remainingMs));
    }
    suppressWarningUntilRef.current = Date.now() + suppressMs;
    // Mark dismiss suppression to avoid the modal reappearing until suppression expires
    dismissSuppressRef.current = true;
    const buffer = 100;
    const clearTime = suppressMs + buffer;
    const clearAt = Date.now() + clearTime;
    suppressWarningUntilRef.current = clearAt;

    // If this was a session warning and we know server-side remaining time,
    // schedule automatic logout for when the server timeout elapses so the
    // user still gets logged out even though the modal is hidden.
    try {
      const ss = sessionStateRef.current;
      if (ss && warningType === 'session') {
        const remainingMs = Math.max(0, Math.floor(ss.timeUntilAbsoluteTimeout)) * 1000;
        // schedule logout at remainingMs (plus small buffer)
        if (dismissLogoutTimeoutRef.current) {
          clearTimeout(dismissLogoutTimeoutRef.current);
        }
        dismissLogoutTimeoutRef.current = setTimeout(() => {
          try { localStorage.removeItem('justSignedIn'); } catch {}
          try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}
          handleLogout();
        }, remainingMs + 1000);
      }
    } catch {
      // ignore scheduling failures
    }
    // clear dismiss suppression after the computed time
    setTimeout(() => {
      dismissSuppressRef.current = false;
      if (dismissLogoutTimeoutRef.current) {
        // keep the scheduled logout if present; do not prematurely clear
      }
    }, clearTime);
  }, [DEBUG]);

  // Keep ref in sync to avoid stale closures inside callbacks
  useEffect(() => {
    showWarningModalRef.current = showWarningModal;
  }, [showWarningModal]);

  // If a sign-in is started in another tab or same tab (authInProgress flag),
  // cancel any dismiss-scheduled auto-logout so the auth flow isn't
  // interrupted by the timeout created when the user dismissed the modal.
  useEffect(() => {
    const handleStorageAuth = (e: StorageEvent) => {
      try {
        if (e.key === 'authInProgress' && e.newValue === 'true') {
          if (DEBUG) console.log('[Session] 🔔 Detected authInProgress via storage - cancelling scheduled dismiss logout');
          if (dismissLogoutTimeoutRef.current) {
            clearTimeout(dismissLogoutTimeoutRef.current);
            dismissLogoutTimeoutRef.current = null;
          }
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', handleStorageAuth);
    return () => window.removeEventListener('storage', handleStorageAuth);
  }, [DEBUG]);
  return {
    isRefreshing,
    showWarningModal,
    warningSecondsRemaining,
    warningType,
    handleStayLoggedIn,
    dismissWarning, 
  };
}